import { useTranslation } from 'react-i18next';
import { type FC } from 'react';
import useUser from 'utils/useUser';
import { isAgencyDoctor } from 'utils/userTypeHelper';

import BaseSelect from '../select/base';

import type { Option } from './base';
import type { AppendFilters } from 'infrastructure/hooks/ccmAgentDashboard/types';

interface Props {
  value: Option[];
  appendFilters: AppendFilters;
  label?: string;
  width?: string;
}

const ScheduledWithAutocomplete: FC<Props> = ({
  appendFilters,
  value,
  label,
  width,
}) => {
  const { t } = useTranslation();

  const { guid: userGuid, userType } = useUser();

  const defaultOptions = [
    {
      id: 'all',
      value: 'all',
      label: t('labels.all'),
    },
    {
      id: 'empty',
      value: 'empty',
      label: t('labels.empty'),
    },
  ];

  if (isAgencyDoctor(userType)) {
    defaultOptions.splice(1, 0, {
      id: userGuid,
      value: userGuid,
      label: t('labels.me'),
    });
  }

  const handleOnChange = (options: Option | Option[]) => {
    const nextAppointmentScheduledWithDoctors: Option[] = [];
    const nextAppointmentScheduledWithAgencyDoctors: Option[] = [];

    let items = Array.isArray(options) ? options : [options];
    const isAllSelected = items.some((item) => item.value === 'all');
    if (isAllSelected) {
      appendFilters({
        nextAppointmentScheduledWithDoctors: [],
        nextAppointmentScheduledWithAgencyDoctors: [],
      });
      return;
    }

    if (items.at(0)?.value === 'empty' && items.length > 1) {
      items = items.filter((item) => item.value !== 'empty');
    }

    const emptyValue = items.find((item) => item.value === 'empty');
    if (emptyValue) {
      appendFilters({
        nextAppointmentScheduledWithDoctors: [],
        nextAppointmentScheduledWithAgencyDoctors: [emptyValue],
      });
      return;
    }

    if (items.at(0)?.value === userGuid) {
      nextAppointmentScheduledWithAgencyDoctors.push(items[0]);
    }

    appendFilters({
      nextAppointmentScheduledWithDoctors,
      nextAppointmentScheduledWithAgencyDoctors,
    });
  };

  return (
    <BaseSelect
      width={width}
      label={label || t('labels.nextAppointmentScheduledWith')}
      placeholder={t('labels.all')}
      mode="multiple"
      options={defaultOptions}
      value={value}
      onChange={handleOnChange}
    />
  );
};

export default ScheduledWithAutocomplete;
